import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { isToday } from 'date-fns';
import { useClient } from 'urql';
import { useNoticeBannersStackContext } from '@sg/garnish';
import { type DeliveryOrderDetail, NotificationType } from '@sg/graphql-schema';

import {
  adjustWantedTime,
  type OrderingContext,
  sumLedgerTotal,
} from '@order/features/ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';
import { ignoreTimezone } from '@order/utils';

import { type PartialLineItem } from '../../../../graphql';
import {
  useNotificationType,
  useOrderStatusLiveActivity,
} from '../../../../integrations';
import { type SubmitBagOrderMutationVariables } from '../../GraphQL/BagSubmitOrder.generated';
import {
  SubmitBagOrderV2Document,
  type SubmitBagOrderV2Mutation,
  type SubmitBagOrderV2MutationVariables,
} from '../../GraphQL/BagSubmitOrderV2.generated';
import { getCheckoutTelemetryData } from '../../utils';
import { type useDefaultPaymentMethod } from '../useDefaultPaymentMethod';
import { usePostOrder } from '../usePostOrder';

// ────────────────────────────────────────────────────────────────────────────

/**
 * A hook for submitting an order.
 */
export const useSubmitOrder = (
  setDefaultPaymentMethod: ReturnType<
    typeof useDefaultPaymentMethod
  >['setDefaultPaymentMethod'],
) => {
  const { track } = useTelemetry();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const getNotificationType = useNotificationType();
  const { startOrderStatusLiveActivity } = useOrderStatusLiveActivity();
  const isTimeslotUnavailableModalEnabled = useFeatureFlag('CELS-4552-timeslot-unavailable-modal-enabled'); // prettier-ignore

  const handlePostOrder = usePostOrder();

  // ─── Mutations ────────────────────────────────────────────────────────────

  const submitOrderMutation = useSubmitOrderMutation();

  const submitOrder = useCallback(
    async (context: OrderingContext<PartialLineItem, DeliveryOrderDetail>) => {
      const {
        cart,
        paymentMethodId = '',
        dropoffLocation: dropoffLocationId,
        wantedTime,
        selectedTip,
        availableCredit,
        shouldUseCredit,
        shouldIncludeUtensils,
      } = context;

      if (!wantedTime) {
        addNoticeBanner({
          text: formatMessage(messages.missingWantedTime),
          palette: 'caution',
        });

        return {
          success: false,
          isTimeslotUnavailable: false,
          error: 'MissingWantedTime',
        };
      }

      const {
        orderId = '',
        restaurantId = '',
        orderChannel = 'pickup',
        locationName = 'sweetgreen',
        deliveryOrderDetail,
        canTrackOrderStatus,
        ledger,
        lineItems,
      } = cart;

      const { total, subtotal, discountTotal } = sumLedgerTotal(
        ledger,
        selectedTip,
        availableCredit,
        shouldUseCredit,
      );

      const orderWantedTime = wantedTime.time.slice(0, 19).replace(' ', 'T');
      const adjustedOrderWantedTime = adjustWantedTime(wantedTime) ?? '';

      const { notificationType, reason } = getNotificationType({
        canTrackOrderStatus,
        wantedTime: orderWantedTime,
      });

      track('notification_preferences.order_notification_type', {
        orderId,
        notificationType,
        notificationTypeReason: reason,
      });

      const result = await submitOrderMutation({
        dropoffLocationId,
        orderId,
        paymentMethodId,
        pickupTip: selectedTip,
        notificationType,
        wantedTime: orderWantedTime,
        useCredit: shouldUseCredit,
        includeUtensils: shouldIncludeUtensils,
        deliveryOrderDetail:
          orderChannel === 'delivery' && deliveryOrderDetail.address?.id
            ? {
                addressId: deliveryOrderDetail.address.id,
                deliveryFee: deliveryOrderDetail.deliveryFee,
                vendor: deliveryOrderDetail.vendor,
                vendorRestaurantId: deliveryOrderDetail.vendorRestaurantId,
                tip: selectedTip,
              }
            : undefined,
      });

      const typename = result?.__typename;

      if (typename === 'SubmitOrderUsingPaymentMethodSuccess') {
        track('checkout.success', getCheckoutTelemetryData(context));

        void setDefaultPaymentMethod(paymentMethodId);

        if (notificationType === NotificationType.LiveActivity) {
          startOrderStatusLiveActivity({
            orderId,
            orderChannel,
            canTrackOrderStatus,
            locationName,
            wantedTime: adjustedOrderWantedTime,
          });
        }

        handlePostOrder({
          restaurantId,
          orderId,
          orderChannel,
          wantedTime: adjustedOrderWantedTime,
          subtotal,
          discountTotal,
          total,
          lineItems,
        });

        return {
          order: result.order,
          success: true,
          isTimeslotUnavailable: false,
          error: undefined,
        };
      }

      track('checkout.failure', { systemError: typename });

      const isNextTimeslotToday = !checkIsNextTimeslotInFuture(context);

      if (
        typename === 'TimeslotUnavailable' &&
        isNextTimeslotToday &&
        !isTimeslotUnavailableModalEnabled
      ) {
        addNoticeBanner({
          text: formatMessage(messages.timeslotUnavailable),
          palette: 'neutral',
        });

        return {
          success: false,
          isTimeslotUnavailable: true,
          error: 'TimeslotUnavailable',
        };
      }

      if (typename === 'TimeslotUnavailable') {
        return {
          success: false,
          isTimeslotUnavailable: true,
          error: 'TimeslotUnavailable',
        };
      }

      if (typename === 'MinimumDeliverySubtotalNotMet') {
        addNoticeBanner({
          palette: 'caution',
          text: formatMessage(messages.minimumDeliverySubtotalNotMet),
        });

        return {
          success: false,
          isTimeslotUnavailable: false,
          error: 'MinimumDeliverySubtotalNotMet',
        };
      }

      if (typename === 'ProductUnavailable') {
        addNoticeBanner({
          text: result.message,
          palette: 'caution',
        });

        return {
          success: false,
          isTimeslotUnavailable: false,
          error: 'ProductUnavailable',
        };
      }

      addNoticeBanner({
        text: formatMessage(messages.orderSubmissionError, { typename }),
        palette: 'caution',
      });

      return {
        success: false,
        isTimeslotUnavailable: false,
        error: undefined,
      };
    },
    [
      getNotificationType,
      track,
      submitOrderMutation,
      isTimeslotUnavailableModalEnabled,
      addNoticeBanner,
      formatMessage,
      setDefaultPaymentMethod,
      handlePostOrder,
      startOrderStatusLiveActivity,
    ],
  );

  // ──────────────────────────────────────────────────────────────────────────

  return { submitOrder };
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const useSubmitOrderMutation = () => {
  const client = useClient();

  return useCallback(
    async (input: SubmitBagOrderMutationVariables['input']) => {
      const mutation = client.mutation<
        SubmitBagOrderV2Mutation,
        SubmitBagOrderV2MutationVariables
      >;

      const response = await mutation(
        SubmitBagOrderV2Document,
        { input },
        { requestPolicy: 'network-only' },
      ).toPromise();

      return response.data?.submitOrderUsingPaymentMethod;
    },
    [client.mutation],
  );
};

const checkIsNextTimeslotInFuture = (
  context: OrderingContext<PartialLineItem, DeliveryOrderDetail>,
) => {
  const nextTimeslot = context.cart.wantedTimes[0];
  const adjustedTime = adjustWantedTime(nextTimeslot);
  const validTime = adjustedTime ? ignoreTimezone(adjustedTime) : null;

  return Boolean(validTime && !isToday(validTime));
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  missingWantedTime: {
    defaultMessage: 'Please select a time for this order',
    description: 'Bag | Order submission error | Missing wanted time',
  },
  orderSubmissionError: {
    defaultMessage: `{typename, select,
    IncorrectPaymentMethod {The card information provided is invalid.
If the issue persists, please contact your card issuer for further assistance.}
    DeclinedPaymentMethod {We're sorry but this transaction has been declined.
Please contact your card issuer or choose an alternative payment method.}
    InvalidCustomerPhone {Your phone number is invalid. Please use a 10 digit US phone number, without country code.}
    InvalidTip {Selected tip cannot be applied. Please choose another option.}
    DeliveryAlreadyInFlight {Sorry, you already have an active delivery order. Please try Pickup, or check the status of your active delivery order.}
    DeliverySpecifyAddress {More than one result was returned for this address. Please make this address as specific as possible.}
    other {Something went wrong, please try again later.}
  }`,
    description: 'Bag | Order submission error',
  },
  minimumDeliverySubtotalNotMet: {
    defaultMessage: `You haven't met the minimum subtotal for delivery.`,
    description:
      'Bag | Order submission error | Minimum delivery subtotal not met',
  },
  timeslotUnavailable: {
    defaultMessage:
      'The selected timeslot window is no longer available. Your order has been updated to the next available time.',
    description: 'Bag | Order submission error | Timeslot unavailable',
  },
});
